import React from "react";
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from "lucide-react";

/**
 * Reusable pagination bar — First / Prev / clickable pages / Next / Last.
 * Shows up to 7 page buttons with an ellipsis when the range is large.
 *
 * Props:
 *   page (0-indexed)
 *   totalPages
 *   onPageChange(nextPage)
 *   totalItems (optional — shown as "N items")
 *   pageSize (optional — informative)
 */
export default function Pagination({ page, totalPages, onPageChange, totalItems, pageSize, testid = "pagination" }) {
  if (totalPages <= 1) return null;
  const go = (p) => onPageChange(Math.min(Math.max(0, p), totalPages - 1));

  // Build visible page list with ellipsis logic
  const window = 5; // odd number of numbered buttons around current
  const half = Math.floor(window / 2);
  let start = Math.max(0, page - half);
  let end = Math.min(totalPages, start + window);
  start = Math.max(0, end - window);
  const pages = [];
  for (let i = start; i < end; i++) pages.push(i);

  const btn = "w-8 h-8 rounded-md text-xs font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center";
  const inactive = "bg-white border-[var(--border)] text-gray-600 hover:border-gray-400";
  const active = "bg-[var(--ink)] text-white border-[var(--ink)]";

  return (
    <div className="flex items-center justify-between gap-3 py-2 px-3 border-t border-[var(--border)] bg-[var(--bg-soft)]" data-testid={testid}>
      {totalItems != null && (
        <span className="text-xs text-gray-500 tabular-nums">
          {totalItems.toLocaleString()} item{totalItems === 1 ? "" : "s"}
          {pageSize && ` · ${pageSize}/page`}
        </span>
      )}
      <div className="flex items-center gap-1 ml-auto">
        <button className={`${btn} ${inactive}`} onClick={() => go(0)} disabled={page === 0} data-testid={`${testid}-first`}>
          <ChevronsLeft size={14} />
        </button>
        <button className={`${btn} ${inactive}`} onClick={() => go(page - 1)} disabled={page === 0} data-testid={`${testid}-prev`}>
          <ChevronLeft size={14} />
        </button>
        {start > 0 && <span className="text-xs text-gray-400 px-1">…</span>}
        {pages.map((p) => (
          <button
            key={p}
            className={`${btn} ${p === page ? active : inactive}`}
            onClick={() => go(p)}
            data-testid={`${testid}-page-${p + 1}`}
          >{p + 1}</button>
        ))}
        {end < totalPages && <span className="text-xs text-gray-400 px-1">…</span>}
        <button className={`${btn} ${inactive}`} onClick={() => go(page + 1)} disabled={page >= totalPages - 1} data-testid={`${testid}-next`}>
          <ChevronRight size={14} />
        </button>
        <button className={`${btn} ${inactive}`} onClick={() => go(totalPages - 1)} disabled={page >= totalPages - 1} data-testid={`${testid}-last`}>
          <ChevronsRight size={14} />
        </button>
      </div>
    </div>
  );
}
